// Building add_patient_lab_tests/'s test[] from care-plan entries.
//
// Extracted verbatim from CarePlanActivityModal (its useCallback had [] deps,
// so this was already pure) because the voice assistant has to produce the SAME
// rows for the same investigation. main/ipd/medication_order.py states the rule
// for the medication side of the house, and it applies just as much here:
//
//   "Both entry endpoints MUST produce identical rows for equivalent input
//    -- do not fork this per caller."
//
// The manual form supplies charge-master metadata (__billing_charge /
// __service_meta) and the voice review screen does not; the plain-field
// fallback below is what lets a dictated test still reach the lab dashboard,
// with an empty code/medical_service_id rather than an invented one.
//
// Import-free so it stays unit-testable: pulling in api/* would drag axios's
// ESM build into Jest and stop the suite running.


// [LAB_TEST] The lab and radiology dashboards match priority by exact string (their
// Priority filters compare `o.priority !== priorityFilter` against "Routine" / "Urgent" /
// "STAT"), while care_plan_structures supplies its options lowercase. Normalize on the way
// out so an order placed here is filterable there. Empty → "Routine", the same default
// both dashboards fall back to when the field is missing.
export const toWirePriority = (raw) => {
  const value = String(raw || "").trim();
  if (!value) return "Routine";
  const lower = value.toLowerCase();
  if (lower === "stat" || lower === "asap") return lower.toUpperCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

// [LAB_TEST] Builds test[] for add_patient_lab_tests/ from entries.
//   • Standalone "test" activity → charge-master selection on the entry root
//     (__billing_charge).
//   • "orders" → Investigation tab → medical-service selections captured on
//     each table row under __service_meta, nested inside
//     test_order[].test_items[]. We walk the structure to find them.
export function buildLabTestsFromEntries(entries) {
  const list = Array.isArray(entries) ? entries : [entries];
  const tests = [];
  const seen = new Set();
  const TEST_ITEM_NAME_FIELDS = [
    "test_name",
    "name",
    "investigation",
    "test",
    "order_detail",
    "orderDetail",
  ];

  const pushTest = (name, code, medicalServiceId, priority) => {
    const trimmedName = String(name || "").trim();
    if (!trimmedName) return;
    const dedupeKey = String(medicalServiceId || code || trimmedName);
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    tests.push({
      name: trimmedName,
      code: String(code || ""),
      medical_service_id: String(medicalServiceId ?? ""),
      // add_patient_lab_tests/ creates one order per test[] entry, and both dashboards
      // read priority off the order (IpdLabOrders/IpdRadiologyOrders mapApiOrder), so it
      // belongs on the item. The form collects one priority for the whole order, which
      // is why every test from a single save carries the same value.
      priority: toWirePriority(priority),
    });
  };

  // Charge-master selection (Test activity).
  list.forEach((entry) => {
    const charge = entry?.__billing_charge;
    if (charge?.name) {
      pushTest(charge.name, charge.code || charge.service_code, charge.medical_service_id, entry?.priority);
    }
  });

  const processTestItemRow = (row, orderPriority) => {
    if (!row || typeof row !== "object") return;
    // Fields already covered by __service_meta carry the real code/
    // medical_service_id — skip them in the plain-field fallback below so a
    // row with a service selection doesn't get pushed twice (once with the
    // real id, once with an empty one from the bare display-text field).
    const metaFields = new Set();
    if (row.__service_meta && typeof row.__service_meta === "object") {
      Object.entries(row.__service_meta).forEach(([field, meta]) => {
        metaFields.add(field);
        if (meta?.name) {
          pushTest(meta.name, meta.code, meta.medical_service_id, orderPriority);
        }
      });
    }
    const plainField = TEST_ITEM_NAME_FIELDS.find(
      (field) =>
        !metaFields.has(field) &&
        row[field] != null &&
        String(row[field]).trim() !== "",
    );
    if (plainField) {
      pushTest(
        row[plainField],
        row.code ?? row.service_code ?? row.snomed_code ?? "",
        row.medical_service_id ?? row.medicalServiceId ?? row.id ?? "",
        orderPriority,
      );
    }
  };

  // Orders → Investigation: __service_meta on dynamic-select rows, plus plain
  // test_items.test_name rows (reuse previous / saved care plan data).
  // Priority is carried down rather than read at the row: the Investigation form puts
  // one Priority select on the test_order group, while the tests themselves live a level
  // deeper in test_items[] / __service_meta.
  const collectInvestigations = (node, inheritedPriority) => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) {
      node.forEach((child) => collectInvestigations(child, inheritedPriority));
      return;
    }

    const priority = String(node.priority || "").trim() || inheritedPriority;

    if (Array.isArray(node.test_items)) {
      node.test_items.forEach((row) => processTestItemRow(row, priority));
    } else if (node.__service_meta && typeof node.__service_meta === "object") {
      Object.values(node.__service_meta).forEach((meta) => {
        if (meta?.name) {
          pushTest(meta.name, meta.code, meta.medical_service_id, priority);
        }
      });
    }

    Object.entries(node).forEach(([key, child]) => {
      if (key === "test_items") return;
      collectInvestigations(child, priority);
    });
  };
  collectInvestigations(list);


  return tests;
}
